import { useCallback, useMemo } from 'react';

import { useIsClient } from 'hooks/useIsClient';
import { useLocalStorage } from 'hooks/useLocalStorage';

export const IDEALIST_NOTIFICATIONS_KEY = 'idealistNotifications';

export enum NotificationStatus {
  NEW = 'NEW',
  DISMISSED = 'DISMISSED',
}

export const NOTIFICATIONS = {
  UPLOAD_RESUME: 'UPLOAD_RESUME',
  ORG_ADMIN_ATS_SURVEY_BANNER: 'ORG_ADMIN_ATS_SURVEY_BANNER',
  ORG_ADMIN_USER_DROPDOWN_ATS_CTA: 'ORG_ADMIN_USER_DROPDOWN_ATS_CTA',
  ORG_ADMIN_SURVEY_BANNER_2022: 'ORG_ADMIN_SURVEY_BANNER_2022',
  SALARY_SURVEY_CTA: 'SALARY_SURVEY_CTA',
} as const;

export type NotificationKey =
  (typeof NOTIFICATIONS)[keyof typeof NOTIFICATIONS];

export type LocalNotification = {
  status: NotificationStatus;
  read?: boolean;
};

export type MappedLocalNotification = LocalNotification & {
  type: NotificationKey;
};

export type LocalNotifications = {
  [k in NotificationKey]?: LocalNotification;
};

/**
 *
 * A hook that returns localStorage notifications and a function to update them.
 * This will always return an empty object if localStorage is not set.
 */
export function useLocalNotifications() {
  const isClient = useIsClient();

  const [notifications, setNotifications] = useLocalStorage<LocalNotifications>(
    IDEALIST_NOTIFICATIONS_KEY,
  );

  // Transforms data stored as:
  // {
  //   "NOTIFICATION_TYPE": {
  //     status: "NEW"
  //   }
  // }
  //
  // to this format:
  // [
  //   {
  //     type: "NOTIFICATION_TYPE",
  //     status: "NEW"
  //   }
  // ]
  const localNotifications = useMemo(() => {
    // Default to an empty array if notifications are not ready yet.
    if (!isClient || notifications === null) return [];

    return Object.entries(notifications as LocalNotifications).map(
      ([key, value]) =>
        ({
          type: key as NotificationKey,
          ...value,
        }) as MappedLocalNotification,
    );
  }, [notifications, isClient]);

  /**
   * A boolean that indicates if localStorage has been read at least once.
   */
  const isReady = useMemo(
    () => localNotifications !== null,
    [localNotifications],
  );

  const isSet = useCallback(
    (type: NotificationKey) =>
      Boolean(
        localNotifications?.find((notification) => notification.type === type),
      ),
    [localNotifications],
  );

  const setLocalNotifications = useCallback(
    (mappedNotifications: MappedLocalNotification[]) => {
      if (!isClient) return;

      const transformed = mappedNotifications.reduce((acc, notification) => {
        const { type, ...rest } = notification;
        return {
          ...acc,
          ...{ [type]: rest },
        };
      }, {});

      setNotifications(transformed);
    },
    [isClient, setNotifications],
  );

  return {
    localNotifications,
    isReady,
    isSet,
    setLocalNotifications,
  };
}

/**
 *
 * A hook that returns a function to update a localStorage notification.
 */
export function useUpdateLocalNotification() {
  const isClient = useIsClient();

  const [notifications, setNotifications] = useLocalStorage<LocalNotifications>(
    IDEALIST_NOTIFICATIONS_KEY,
  );

  const update = useCallback(
    (name: NotificationKey, value: Partial<MappedLocalNotification>) => {
      if (!isClient) return;

      const currentNotification = notifications ? notifications[name] : {};
      const newNotification = { ...currentNotification, ...value };

      return setNotifications({
        ...notifications,
        [name]: newNotification,
      });
    },
    [notifications, isClient, setNotifications],
  );

  return update;
}
